/* eslint-disable no-console */
/* eslint-disable max-lines */
import 'intl-tel-input/build/css/intlTelInput.css'

import { AA_SESSION_ID, AA_UID, GA_SESSION_ID, GA_UID } from '@site/js/utils/cookies'
import { postRequest } from '@site/js/utils/request'
import { parseJson } from '@site/js/utils/utils'
import intlTelInput from 'intl-tel-input'
import utilsScript from 'intl-tel-input/build/js/utils'

import { loadingSpinnerSvg } from '../loading-spinner/loading-spinner'
import pageTracker from '../page-tracker/page-tracker'
import { checkInputFileValidity, getInputFormFiles, uploadFiles } from './file-upload'
import { retrieveFieldsFromSessionStorage, storeFieldsInSessionStorage } from './form-autofill'
import { formFieldPrefiller } from './form-field-prefiller'
import { initFormTracking, trackFormErrors, trackFormSuccess } from './form-tracking'
import {
  getBackendValidationErrors,
  getFrontendValidationErrors,
  printFormValidationErrors,
  printFormValidationSuccess,
  resetElementValidation,
  resetGroupValidation,
} from './form-validation'
import { phoneCountriesOptions } from './phonecountriesoptions'

const prePopulationValues = {
  prePopulatedFields: document.querySelectorAll('[data-pre-populated-with]'),
  pageUrl: 'previous-page-url',
}

const inputTypeFileSelector = 'input[type="file"]'

/*
  Snippet which takes all url query parameters, checks whether parameter name matches
  with form field name attribute and if so , prefills field value with url parameter value
*/
const prefillFormFields = () => {
  const formElements = document.querySelectorAll('.cmp-form')

  if (Array.from(formElements).length === 0) {
    return
  }

  const url = new URL(window.location.href)
  const urlParameters = url.searchParams

  Array.from(urlParameters.entries()).forEach(urlEntryParam => {
    const [urlParamName, urlParamValue] = urlEntryParam
    const formFieldElementByName = document.querySelector(`form [name="${urlParamName}"]`)

    if (formFieldElementByName) {
      formFieldPrefiller(formFieldElementByName, urlParamValue)

      urlParameters.delete(urlParamName)
    }
  })

  // Cleaning browser address bar from parameters
  url.search = urlParameters.toString()
  window.history.pushState(null, '', url.toString())
}

prefillFormFields()

document.addEventListener('sessionDataReady', function (e) {
  try {
    const { aa_uid, aa_session_id, ga_uid, ga_session_id } = e.detail.message

    if (!prePopulationValues.prePopulatedFields) {
      return
    }

    prePopulationValues.prePopulatedFields.forEach(item => {
      const itemAttrValue = item.getAttribute('data-pre-populated-with')

      switch (itemAttrValue) {
        case prePopulationValues.pageUrl: {
          item.value = pageTracker.previousUrl()
          break
        }
        case AA_SESSION_ID:
          item.value = aa_session_id || ''
          break
        case AA_UID:
          item.value = aa_uid || ''
          break
        case GA_SESSION_ID:
          item.value = ga_session_id || ''
          break
        case GA_UID:
          item.value = ga_uid || ''
          break
        case 'previous-page-industry-tag':
          {
            const splitCharacter = pageTracker.previousIndustryTag()?.includes('/') ? '/' : ':'
            item.value = pageTracker.previousIndustryTag()?.split(splitCharacter).at(-1)
          }
          break
        case 'support_type': {
          item.value = pageTracker.supportType()
          break
        }
        default: {
          const splitCharacter = pageTracker.previousInquiryTag()?.includes('/') ? '/' : ':'
          item.value = pageTracker.previousInquiryTag()?.split(splitCharacter).at(-1)
        }
      }
    })
  } catch (e) {
    console.error('[form]: An error has occurred on sessionDataReady event', e)
  }
})

const setAriaLabelsToRadios = () => {
  const radios = document.querySelectorAll('.cmp-form-options__field--radio')
  radios.forEach(radio => {
    const radioParent = radio.closest('.cmp-form-options__field-label')
    const labelContent = radioParent.querySelector('.cmp-form-options__field-description').innerText
    if (labelContent) {
      radio.setAttribute('aria-label', labelContent)
    }
  })
}

setAriaLabelsToRadios()

const prefillSelectbox = (form, prePopulatedField, tag) => {
  const afterNamespace = tag?.split(':')
  if (!afterNamespace || afterNamespace?.length < 2) {
    const prePopulationFields = form.querySelectorAll(`[data-pre-populated-with=${prePopulatedField}] ~ .c-custom-select-panel [data-value]`)
    if (prePopulationFields?.length) {
      const sublevel = prePopulationFields[0].querySelector('.c-custom-select-arrow')
      if (sublevel) {
        sublevel.click()
        prePopulationFields[1].click()
      } else {
        prePopulationFields[0].click()
      }
    }
    return
  }

  const tagAsList = afterNamespace[1].split('/')
  const selectElements = []

  tagAsList.forEach(item => {
    const prePopulationFields = form.querySelectorAll(`[data-pre-populated-with=${prePopulatedField}] ~ .c-custom-select-panel [data-value=${item}]`)
    prePopulationFields?.forEach(current => {
      current.click()
      if (item === tagAsList.at(-1)) {
        selectElements.push({ current, value: item })
      }
    })
  })

  window.addEventListener('load', () => {
    selectElements?.forEach(selectElement => {
      const currentSelectElement = selectElement.current.closest('.customSelect').querySelector('[data-pre-populated-with]')
      if (currentSelectElement.value !== selectElement.value) {
        currentSelectElement.value = selectElement.value
      }
    })
  })
}

/**
 * Script checks whether URL contains inqiury or industry tag url fragment
 * and if so, makes following actions:
 * Preselects inqiury or industry tag dropdown with this value.
 */
const prefillPageTrackingSelectboxes = form => {
  if (!prePopulationValues.prePopulatedFields) {
    return
  }

  const previousPageTags = [
    {
      field: 'previous-page-inquiry-tag',
      value: pageTracker.previousInquiryTag(),
    },
    {
      field: 'previous-page-industry-tag',
      value: pageTracker.previousIndustryTag(),
    },
  ]

  previousPageTags.forEach(tag => {
    prefillSelectbox(form, tag.field, tag.value)
  })

  const supportTypeHiddenInput = form.querySelector('[data-pre-populated-with="support_type"]')
  if (supportTypeHiddenInput && !supportTypeHiddenInput.value) {
    supportTypeHiddenInput.value = pageTracker.supportType()
  }
}

/**
 * Form Component
 */
const FormComponent = () => {
  const formItemList = document.querySelectorAll('.cmp-form input, .cmp-form textarea, .cmp-form select')
  const formButtonList = document.querySelectorAll('.cmp-form-button--wrapper')
  const formPhoneList = document.querySelectorAll('[type=tel]')
  const formFileList = document.querySelectorAll('[type=file]')
  const formFileWrapperDivs = document.querySelectorAll('.cmp-form-file')
  let formSubmitLabel

  const printFormSending = form => {
    const submitButton = form.querySelector('.cmp-form-button')
    submitButton.disabled = true
    // label update
    formSubmitLabel = submitButton.textContent
    submitButton.textContent = parseJson(form.dataset.translations)?.formSendingLabel

    showLoadingSpinner(submitButton)
  }

  const printFormNotSending = form => {
    const submitButton = form.querySelector('.cmp-form-button')
    submitButton.disabled = false
    const loadingSpinnerSpan = submitButton.parentNode.querySelector('.cmp-form-button--loading')
    if (loadingSpinnerSpan) {
      loadingSpinnerSpan.style.display = ''
    }
    // label update
    submitButton.textContent = formSubmitLabel
  }

  const showLoadingSpinner = button => {
    let loadingSpinnerSpan = button.querySelector('.cmp-form-button--loading')
    if (!loadingSpinnerSpan) {
      loadingSpinnerSpan = document.createElement('span')
      loadingSpinnerSpan.classList.add('cmp-form-button--loading', 'cmp-button__inline-svg-icon')
      loadingSpinnerSpan.innerHTML = loadingSpinnerSvg
      loadingSpinnerSpan.querySelector('rect').setAttribute('rx', 0)
      button.prepend(loadingSpinnerSpan)
    }
    if (loadingSpinnerSpan) {
      loadingSpinnerSpan.style.display = 'flex'
    }
  }

  const updateRemainingCharacters = element => {
    const parentElement = element.parentElement
    const remainingCharactersElement = parentElement.querySelector('.cmp-form-text__characters-remaining--current-number-of-characters')
    remainingCharactersElement.textContent = element.value.length
  }

  const printFieldsReadonly = form => {
    form.querySelectorAll('input, textarea, select').forEach(el => el.setAttribute('readonly', true))
    form.querySelectorAll('input[type="radio"]').forEach(el => el.setAttribute('disabled', true))
    form.querySelectorAll(inputTypeFileSelector).forEach(el => el.setAttribute('disabled', true))
    form.querySelectorAll('.cmp-form-file .cmp-form-file__button').forEach(el => el.setAttribute('disabled', true))
    form.querySelectorAll('.c-custom-select-opener').forEach(el => el.classList.add('c-custom-select-opener--read-only'))
  }

  const printFieldsEditable = form => {
    form.querySelectorAll('input, textarea, select').forEach(el => el.removeAttribute('readonly'))
    form.querySelectorAll('input[type="radio"]').forEach(el => el.removeAttribute('disabled'))
    form.querySelectorAll(inputTypeFileSelector).forEach(el => el.removeAttribute('disabled'))
    form.querySelectorAll('.cmp-form-file .cmp-form-file__button').forEach(el => el.removeAttribute('disabled', true))
    form.querySelectorAll('.c-custom-select-opener').forEach(el => el.classList.remove('c-custom-select-opener--read-only'))
  }

  const getFormData = async form => {
    const body = new FormData(form)
    const fileInputs = form.querySelectorAll(inputTypeFileSelector)

    if (fileInputs?.length) {
      const arrayOfFileInputs = Array.from(fileInputs)
      const fileContentPromises = arrayOfFileInputs.map(getInputFormFiles)

      const allFilesPromises = await Promise.all(fileContentPromises)

      arrayOfFileInputs.forEach(fileInput => {
        allFilesPromises.forEach(promise => {
          if (promise.key === fileInput.name) {
            body.delete(fileInput.name)
            body.append(`${fileInput.name}`, promise.content)
            body.append(`${fileInput.name}_filename`, promise.fileName)
          }
        })
      })
    }

    return body
  }

  const apiFormSubmit = async form => {
    const apiUrl = document.querySelector('.cmp-form').dataset.apiUrl
    try {
      const formData = await getFormData(form)
      printFormSending(form)
      printFieldsReadonly(form)
      postRequest(apiUrl, formData)
        .then(async response => {
          let responseData
          let isFormSubmissionSuccessful

          if (response.redirected) {
            responseData = null
          } else {
            responseData = await response.json()
          }

          const formName = form.getAttribute('name')

          if (response.redirected) {
            isFormSubmissionSuccessful = formName === 'Mail'
          } else if (response.status === 200) {
            isFormSubmissionSuccessful = responseData.valid
          }

          if (isFormSubmissionSuccessful) {
            trackFormSuccess(form)
            printFormValidationSuccess(form)
          } else {
            printFieldsEditable(form)
            const backendValidationErrors = getBackendValidationErrors(form, responseData)
            trackFormErrors(form, backendValidationErrors)
            printFormValidationErrors(backendValidationErrors)
          }
        })
        .catch(error => {
          printFormValidationErrors([])
          console.error('apiFormSubmit', error)
        })
        .finally(() => {
          printFormNotSending(form)
          printFieldsEditable(form)
        })
    } catch (e) {
      console.error(e)
    }
  }

  /**
   * Before submit the form, it checks if form inputs are valid
   * @param {event} e event of the button that was clicked
   */
  const logSubmit = e => {
    e.preventDefault()

    const form = e.target.closest('.cmp-form')

    if (formFileList) {
      formFileList.forEach(fileItem => {
        resetElementValidation(fileItem)
        checkInputFileValidity(fileItem)
      })
    }

    if (!form.checkValidity()) {
      const frontendValidationErrors = getFrontendValidationErrors(form)
      trackFormErrors(form, frontendValidationErrors)
      printFormValidationErrors(frontendValidationErrors)
      return false
    }

    const dataFormAutoFillEl = document.querySelector('[data-form-autofill]')
    if (dataFormAutoFillEl && dataFormAutoFillEl.dataset.formAutofill === 'true') {
      storeFieldsInSessionStorage(form)
    }

    apiFormSubmit(form)
  }

  const phoneCountryPreselect = () => {
    const selectedOption = document.querySelector('.cmp-countriesoptions__country .c-custom-select-option.is-selected')
    const selectedOptionCode = selectedOption?.getAttribute('data-country-code')
    const selectedOptionCountryCode = selectedOptionCode?.toLowerCase()
    if (selectedOptionCountryCode !== undefined) {
      formPhoneList.forEach(phone => phone.iti.setCountry(selectedOptionCountryCode))
    }
  }

  /**
   * Set up split-code phone fields
   */
  const initPhoneInput = el => {
    el.name = 'phone-input'
    const inputName = el.dataset?.inputName || 'phone'
    el.iti = intlTelInput(el, {
      geoIpLookup: success => success(''), // return no country, default no selection
      hiddenInput: inputName,
      initialCountry: 'auto',
      localizedCountries: {
        tw: 'Taiwan, China',
      },
      onlyCountries: phoneCountriesOptions,
      preferredCountries: [],
      separateDialCode: true,
      formatOnDisplay: false,
      utilsScript,
    })
    el.iti.promise.then(() => {
      const parent = el.closest('.cmp-form-text')
      const hiddenPhone = parent.querySelector(`[name=${inputName}]`)
      hiddenPhone.classList.add('cmp-form-text__text', 'hidden')
      hiddenPhone.setAttribute('data-cmp-hook-form-text', 'input')
      hiddenPhone.setAttribute('data-input-type', 'tel')
      hiddenPhone.setAttribute('type', 'hidden')
      hiddenPhone.setAttribute('autocomplete', 'off')
      hiddenPhone.setAttribute('pattern', '\\+[0-9]{4,15}')
      hiddenPhone.setAttribute('required', 'true')
      el.placeholder = JSON.parse(parent.dataset.translations)?.selectCountryCode
      el.addEventListener('input', () => {
        hiddenPhone.value = el.iti.getNumber()
      })
      el.addEventListener('countrychange', () => {
        el.placeholder = ''
        hiddenPhone.value = el.iti.getNumber()
      })
    })
  }

  /**
   * initialize
   */
  const init = function () {
    formButtonList.forEach(item => {
      item.addEventListener('click', e => {
        this.logSubmit(e)
      })
    })
    formPhoneList.forEach(phone => initPhoneInput(phone))
    formItemList.forEach(item => {
      const dataFormAutoFillEl = document.querySelector('[data-form-autofill]')
      if (dataFormAutoFillEl && dataFormAutoFillEl.dataset.formAutofill === 'true') {
        retrieveFieldsFromSessionStorage(item)
      }
      item.addEventListener('change', e => {
        const countrySelect = document.querySelector('.cmp-countriesoptions__country select')
        if (item === countrySelect) {
          const stateSelectContainer = document.querySelector('.cmp-countriesoptions .cmp-countriesoptions__state')
          stateSelectContainer.classList.remove('is-correct')
          stateSelectContainer.classList.remove('is-empty')
          phoneCountryPreselect()
        }
        if (e.target.type === 'checkbox') {
          const checkboxes = e.target.closest('.cmp-form-options').querySelectorAll('.cmp-form-options__field--checkbox')

          let checkStatus = []
          checkboxes.forEach(checkbox => {
            checkStatus.push(checkbox.checked)
          })

          checkboxes.forEach(checkbox => {
            if (checkStatus.includes(true)) {
              checkbox.required = false
            } else {
              checkbox.required = true
            }
          })
        }
        resetElementValidation(e.target)
        resetGroupValidation(e.target)
      })
      if (item.parentElement.dataset.displayCharactersCountdown === 'true') {
        item.addEventListener('input', event => {
          updateRemainingCharacters(event.target)
        })
      }
    })
    const formsInDocument = document.querySelectorAll('.cmp-form')
    if (formsInDocument) {
      formsInDocument.forEach(form => {
        prefillPageTrackingSelectboxes(form)
        initFormTracking(form)
      })
    }
    formFileList?.forEach(item => {
      item.addEventListener('change', e => uploadFiles(e))
    })
    formFileWrapperDivs?.forEach(item => {
      item.querySelector('.cmp-form-file__button').addEventListener('click', () => {
        item.querySelector(inputTypeFileSelector).click()
      })
    })
  }

  return {
    logSubmit,
    resetElementValidation,
    resetGroupValidation,
    init,
  }
}

export { FormComponent }
/* eslint-enable max-lines */
